import { Controller } from "@hotwired/stimulus"

const isCheckable = (element) => ["checkbox", "radio"].includes(element.type)

export default class extends Controller {
  static targets = ["customerCheckbox", "input", "loadingIndicator", "resultsFrame"]
  static values = {
    selectedCustomerIds: Array,
    url: String,
    displayMode: String,
  }

  applyFilter(e) {
    const target = e.currentTarget

    // Multi-value checkbox groups ("name[]") send every checked value
    if (target.name.endsWith("[]")) {
      this.filterParams.delete(target.name)

      this.inputTargets
        .filter((input) => input.name === target.name && input.checked)
        .forEach((input) => this.filterParams.append(target.name, input.value))

      this.submitFilters()
      return
    }

    // Extract attribute name from input name (handles both "name" and "prefix[name]" formats)
    const match = target.name.match(/\[(\w+)\]/)
    const attributeName = match ? match[1] : target.name
    let val = target.value

    if (isCheckable(target)) {
      val = +target.checked
    }

    if (val) {
      this.filterParams.set(attributeName, val)
    } else {
      this.filterParams.delete(attributeName)
    }

    this.submitFilters()
  }

  clearFilters() {
    // Each input's applyFilter would otherwise submit on its own
    this.clearingFilters = true

    this.inputTargets.forEach((input) => {
      if (isCheckable(input)) {
        input.checked = false
      } else {
        input.value = ""
      }

      input.dispatchEvent(new Event("input", { bubbles: true }))
    })

    this.clearingFilters = false
    this.submitFilters()
  }

  connect() {
    this.filterParams = new URLSearchParams(window.location.search)

    if (this.hasResultsFrameTarget) {
      this.resultsFrameTarget.addEventListener("turbo:before-fetch-request", (event) => {
        // This event fires when hovering over the frame's links. This prevents that
        if (event.target != this.resultsFrameTarget) {
          return
        }

        this.toggleLoading(true)
      })

      this.resultsFrameTarget.addEventListener("turbo:frame-load", (event) => {
        this.toggleLoading(false)
        this.restoreSelectedCustomers()
      })
    }
  }

  restoreSelectedCustomers() {
    const selectedIds = new Set(this.selectedCustomerIdsValue.map(String))

    this.customerCheckboxTargets.forEach((checkbox) => {
      if (selectedIds.has(checkbox.value)) {
        checkbox.checked = true
      }
    })
  }

  setSelectedCustomers(e) {
    const selectedIds = new Set(this.selectedCustomerIdsValue.map(String))

    this.customerCheckboxTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        selectedIds.add(checkbox.value)
      } else {
        selectedIds.delete(checkbox.value)
      }
    })

    this.selectedCustomerIdsValue = Array.from(selectedIds)
  }

  submitFilters() {
    if (this.clearingFilters || !this.hasResultsFrameTarget) {
      return
    }

    // Ensure display_mode is always included in filter params
    if (this.displayModeValue) {
      this.filterParams.set("display_mode", this.displayModeValue)
    }

    const url = new URL(this.urlValue, window.location.origin)
    url.search = this.filterParams.toString()

    this.resultsFrameTarget.setAttribute("src", url.pathname + url.search)
  }

  toggleLoading(isVisible) {
    if (!this.hasLoadingIndicatorTarget) {
      return
    }

    if (isVisible) {
      this.loadingIndicatorTarget.classList.remove("hidden")
    } else {
      this.loadingIndicatorTarget.classList.add("hidden")
    }
  }
}
